import React, { useState, useEffect } from 'react';
import {
  Box, Card, Container, Group, Title, Text, Button, Badge, Table, ActionIcon,
  Modal, TextInput, NumberInput, Stack, SimpleGrid, Paper, ThemeIcon, CopyButton,
  Tooltip, Loader, Alert, Tabs
} from '@mantine/core';
import {
  IconCpu, IconKey, IconMail, IconPlus, IconTrash, IconCheck, IconCopy,
  IconShoppingCart, IconSend, IconAlertCircle, IconExternalLink, IconRobot
} from '@tabler/icons-react';

export default function TokensTab({ user, apiBaseUrl, t }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Modals
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [buyModalOpen, setBuyModalOpen] = useState(false);
  const [testModalOpen, setTestModalOpen] = useState(false);

  // New client form state
  const [newEmail, setNewEmail] = useState('');
  const [newTokens, setNewTokens] = useState(100000);
  const [newGeminiKey, setNewGeminiKey] = useState('');
  const [newModel, setNewModel] = useState('gemini-2.5-flash');

  // Buy token form state
  const [buyEmail, setBuyEmail] = useState(user?.email || '');
  const [buyLoading, setBuyLoading] = useState(false);
  const [generatedBuyUrl, setGeneratedBuyUrl] = useState('');

  // Inline editing of Gemini keys: { [clientId]: string }
  const [geminiKeys, setGeminiKeys] = useState({});
  const [savingKeys, setSavingKeys] = useState({});

  // Proxy test state
  const [testToken, setTestToken] = useState('');
  const [testPrompt, setTestPrompt] = useState('Napisz krótkie biznesowe powitanie w języku polskim.');
  const [testLoading, setTestLoading] = useState(false);
  const [testResponse, setTestResponse] = useState('');

  const fetchClients = async () => {
    setLoading(true);
    setError('');
    try {
      const email = user?.email || 'office@ailinkb.shop';
      const res = await fetch(`${apiBaseUrl}/api/tokens/clients?ownerEmail=${encodeURIComponent(email)}`);
      if (res.ok) {
        const data = await res.json();
        setClients(data);
        // Pre-fill geminiKeys state
        const initialKeys = {};
        data.forEach(c => {
          initialKeys[c.id] = c.geminiApiKey || '';
        });
        setGeminiKeys(initialKeys);
      } else {
        setError(t('tokens_error_fetch', 'Nie udało się pobrać listy klientów tokenów.'));
      }
    } catch (e) {
      console.error(e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [user?.email]);

  const handleSaveGeminiKey = async (clientId) => {
    setSavingKeys(prev => ({ ...prev, [clientId]: true }));
    setError('');
    setSuccessMsg('');
    try {
      const apiKey = geminiKeys[clientId] || '';
      const res = await fetch(`${apiBaseUrl}/api/tokens/clients/${clientId}/gemini-key`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ geminiApiKey: apiKey })
      });
      if (res.ok) {
        setSuccessMsg(t('tokens_key_saved', 'Klucz Gemini został pomyślnie zapisany dla tego klienta!'));
        setTimeout(() => setSuccessMsg(''), 4000);
        fetchClients();
      } else {
        alert(t('tokens_key_save_failed', 'Błąd podczas zapisywania klucza Gemini.'));
      }
    } catch (e) {
      alert(e.message);
    } finally {
      setSavingKeys(prev => ({ ...prev, [clientId]: false }));
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    if (!newEmail) return alert(t('tokens_email_required', 'Adres e-mail jest wymagany.'));

    try {
      const res = await fetch(`${apiBaseUrl}/api/tokens/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientEmail: newEmail,
          ownerEmail: user?.email || 'office@ailinkb.shop',
          tokensRemaining: newTokens,
          geminiApiKey: newGeminiKey,
          geminiModel: newModel
        })
      });
      if (res.ok) {
        setAddModalOpen(false);
        setNewEmail('');
        setNewGeminiKey('');
        fetchClients();
      } else {
        alert(t('tokens_create_failed', 'Nie udało się dodać klienta.'));
      }
    } catch (e) {
      alert(e.message);
    }
  };

  const handleSendEmail = async (clientId, email) => {
    if (!window.confirm(`${t('tokens_confirm_send_email', 'Wysłać token e-mailem na adres:')} ${email}?`)) return;
    try {
      const res = await fetch(`${apiBaseUrl}/api/tokens/clients/${clientId}/send-email`, {
        method: 'POST'
      });
      if (res.ok) {
        alert(t('tokens_email_sent_success', 'Wiadomość z tokenem i instrukcją została wysłana do klienta!'));
      } else {
        alert(t('tokens_email_sent_failed', 'Błąd wysyłki e-maila.'));
      }
    } catch (e) {
      alert(e.message);
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm(t('tokens_confirm_delete', 'Czy na pewno chcesz usunąć tego klienta i dezaktywować jego token?'))) return;
    try {
      const res = await fetch(`${apiBaseUrl}/api/tokens/clients/${clientId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        fetchClients();
      }
    } catch (e) {
      alert(e.message);
    }
  };

  const handleGenerateBuyCheckout = async () => {
    if (!buyEmail) return alert(t('tokens_email_required', 'Wpisz e-mail klienta.'));
    setBuyLoading(true);
    setGeneratedBuyUrl('');
    try {
      const res = await fetch(`${apiBaseUrl}/api/tokens/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: buyEmail,
          originUrl: window.location.origin
        })
      });
      if (res.ok) {
        const data = await res.json();
        setGeneratedBuyUrl(data.url);
      } else {
        alert(t('tokens_checkout_failed', 'Błąd tworzenia sesji zakupu tokenów.'));
      }
    } catch (e) {
      alert(e.message);
    } finally {
      setBuyLoading(false);
    }
  };

  const handleRunProxyTest = async () => {
    if (!testToken) return alert(t('tokens_test_token_required', 'Wybierz lub wklej token klienta.'));
    if (!testPrompt) return alert(t('tokens_test_prompt_required', 'Wpisz treść zapytania / prompt.'));

    setTestLoading(true);
    setTestResponse('');
    try {
      const res = await fetch(`${apiBaseUrl}/api/tokens/proxy/gemini`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${testToken}`
        },
        body: JSON.stringify({ prompt: testPrompt })
      });
      const data = await res.json();
      if (res.ok) {
        setTestResponse(data.response || data.text || JSON.stringify(data, null, 2));
        fetchClients(); // refresh usage counters
      } else {
        setTestResponse(`Błąd: ${data.error || 'Nieznany błąd proxy'}`);
      }
    } catch (e) {
      setTestResponse(`Błąd połączenia: ${e.message}`);
    } finally {
      setTestLoading(false);
    }
  };

  // Stats calculation
  const totalClients = clients.length;
  const claimedCount = clients.filter(c => c.claimed).length;
  const totalTokensAvailable = clients.reduce((acc, c) => acc + (c.tokensRemaining || 0), 0);
  const totalRequestsExecuted = clients.reduce((acc, c) => acc + (c.totalRequests || 0), 0);

  return (
    <Container size="xl" py="lg">
      <Stack gap="lg">
        {/* Header Section */}
        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
          <div>
            <Group gap="xs" align="center">
              <ThemeIcon size="lg" radius="md" color="indigo" variant="light">
                <IconCpu size={22} />
              </ThemeIcon>
              <Title order={2} c="indigo.8" style={{ fontWeight: 800 }}>
                {t('tokens_tab_title', 'Zarządzanie Tokenami Klientów AI (Gemini Proxy)')}
              </Title>
            </Group>
            <Text c="dimmed" size="sm" mt={4}>
              {t('tokens_tab_subtitle', 'Lista klientów, zakup tokenów (100 PLN + VAT), konfiguracja kluczy Gemini oraz inteligentny proxy AI.')}
            </Text>
          </div>

          <Group gap="sm">
            <Button
              variant="light"
              color="indigo"
              leftSection={<IconRobot size={18} />}
              onClick={() => {
                if (clients.length > 0 && !testToken) setTestToken(clients[0].clientToken);
                setTestModalOpen(true);
              }}
            >
              {t('tokens_btn_test_proxy', 'Testuj Gemini Proxy')}
            </Button>
            <Button
              variant="outline"
              color="indigo"
              leftSection={<IconShoppingCart size={18} />}
              onClick={() => {
                setGeneratedBuyUrl('');
                setBuyModalOpen(true);
              }}
            >
              {t('tokens_btn_buy', 'Kup Tokeny (100 PLN + VAT)')}
            </Button>
            <Button
              color="indigo"
              leftSection={<IconPlus size={18} />}
              onClick={() => setAddModalOpen(true)}
            >
              {t('tokens_btn_add_client', 'Dodaj Klienta Ręcznie')}
            </Button>
          </Group>
        </Box>

        {/* Success / Error Alerts */}
        {successMsg && (
          <Alert color="teal" icon={<IconCheck size={16} />} title={t('success_label', 'Sukces')} withCloseButton onClose={() => setSuccessMsg('')}>
            {successMsg}
          </Alert>
        )}
        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} title={t('error_label', 'Błąd')} withCloseButton onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Stats Cards */}
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
          <Paper withBorder p="md" radius="md" shadow="xs">
            <Group justify="space-between">
              <Text size="xs" c="dimmed" fw={700} tt="uppercase">{t('tokens_stat_total', 'Łącznie Klientów')}</Text>
              <ThemeIcon color="blue" variant="light" size="sm" radius="md"><IconCpu size={14} /></ThemeIcon>
            </Group>
            <Text fw={700} size="xl" mt="xs">{totalClients}</Text>
          </Paper>

          <Paper withBorder p="md" radius="md" shadow="xs">
            <Group justify="space-between">
              <Text size="xs" c="dimmed" fw={700} tt="uppercase">{t('tokens_stat_claimed', 'Pobrane Tokeny')}</Text>
              <ThemeIcon color="green" variant="light" size="sm" radius="md"><IconCheck size={14} /></ThemeIcon>
            </Group>
            <Group align="baseline" gap="xs">
              <Text fw={700} size="xl" mt="xs">{claimedCount}</Text>
              <Text size="xs" c="dimmed">/ {totalClients}</Text>
            </Group>
          </Paper>

          <Paper withBorder p="md" radius="md" shadow="xs">
            <Group justify="space-between">
              <Text size="xs" c="dimmed" fw={700} tt="uppercase">{t('tokens_stat_balance', 'Dostępne Jednostki')}</Text>
              <ThemeIcon color="indigo" variant="light" size="sm" radius="md"><IconKey size={14} /></ThemeIcon>
            </Group>
            <Text fw={700} size="xl" mt="xs" c="indigo.7">{totalTokensAvailable.toLocaleString()}</Text>
          </Paper>

          <Paper withBorder p="md" radius="md" shadow="xs">
            <Group justify="space-between">
              <Text size="xs" c="dimmed" fw={700} tt="uppercase">{t('tokens_stat_requests', 'Wykonane Zapytania')}</Text>
              <ThemeIcon color="violet" variant="light" size="sm" radius="md"><IconRobot size={14} /></ThemeIcon>
            </Group>
            <Text fw={700} size="xl" mt="xs">{totalRequestsExecuted.toLocaleString()}</Text>
          </Paper>
        </SimpleGrid>

        {/* Main Clients Table */}
        <Card withBorder shadow="sm" radius="md" p={0}>
          <Box p="md" style={{ borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title order={4} c="gray.8">
              {t('tokens_list_heading', 'Lista Klientów & Konfiguracja Kluczy Gemini')}
            </Title>
            <Button size="xs" variant="subtle" color="indigo" onClick={fetchClients} loading={loading}>
              {t('refresh_btn', 'Odśwież')}
            </Button>
          </Box>

          {loading ? (
            <Box p={50} ta="center"><Loader color="indigo" size="md" /></Box>
          ) : clients.length === 0 ? (
            <Box p={50} ta="center">
              <Text c="dimmed">{t('tokens_no_clients', 'Brak zarejestrowanych klientów tokenów AI.')}</Text>
              <Button mt="md" size="sm" color="indigo" variant="light" onClick={() => setAddModalOpen(true)}>
                {t('tokens_add_first_client', 'Dodaj pierwszego klienta')}
              </Button>
            </Box>
          ) : (
            <Table.ScrollContainer minWidth={950}>
              <Table verticalSpacing="sm" horizontalSpacing="md" striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('tokens_col_email', 'Klient (E-mail)')}</Table.Th>
                    <Table.Th>{t('tokens_col_token', 'Token Klienta (AILinkB)')}</Table.Th>
                    <Table.Th>{t('tokens_col_status', 'Status Pobrania')}</Table.Th>
                    <Table.Th>{t('tokens_col_balance', 'Stan Tokenów')}</Table.Th>
                    <Table.Th style={{ width: '300px' }}>{t('tokens_col_gemini_key', 'Klucz Gemini AI (dla tego klienta)')}</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>{t('tokens_col_actions', 'Akcje')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {clients.map((c) => (
                    <Table.Tr key={c.id}>
                      {/* Email */}
                      <Table.Td>
                        <Text fw={600} size="sm">{c.clientEmail}</Text>
                        <Text size="xs" c="dimmed">
                          {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : ''}
                        </Text>
                      </Table.Td>

                      {/* Token with copy */}
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          <Text size="xs" ff="monospace" fw={700} c="indigo.8" style={{ background: '#f8fafc', padding: '4px 8px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                            {c.clientToken}
                          </Text>
                          <CopyButton value={c.clientToken} timeout={2000}>
                            {({ copied, copy }) => (
                              <Tooltip label={copied ? t('copied_label', 'Skopiowano') : t('copy_label', 'Kopiuj')}>
                                <ActionIcon color={copied ? 'teal' : 'gray'} variant="subtle" size="sm" onClick={copy}>
                                  {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </CopyButton>
                        </Group>
                      </Table.Td>

                      {/* Claimed status */}
                      <Table.Td>
                        {c.claimed ? (
                          <Badge color="green" variant="light" size="sm" leftSection={<IconCheck size={12} />}>
                            {t('tokens_status_claimed', 'Pobrany')} ({c.claimedAt ? new Date(c.claimedAt).toLocaleDateString() : ''})
                          </Badge>
                        ) : (
                          <Badge color="yellow" variant="light" size="sm">
                            {t('tokens_status_pending', 'Oczekuje')}
                          </Badge>
                        )}
                      </Table.Td>

                      {/* Balance & Requests */}
                      <Table.Td>
                        <Text size="sm" fw={600} c={c.tokensRemaining > 0 ? 'dark' : 'red'}>
                          {c.tokensRemaining ? c.tokensRemaining.toLocaleString() : 0}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {t('tokens_requests_done', 'Wykonano:')} {c.totalRequests || 0}
                        </Text>
                      </Table.Td>

                      {/* Gemini API Key input and Save */}
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          <TextInput
                            size="xs"
                            placeholder={t('tokens_gemini_key_placeholder', 'Wpisz klucz Gemini AI (AIzaSy...)')}
                            value={geminiKeys[c.id] !== undefined ? geminiKeys[c.id] : (c.geminiApiKey || '')}
                            onChange={(e) => {
                              const val = e.target.value;
                              setGeminiKeys(prev => ({ ...prev, [c.id]: val }));
                            }}
                            style={{ flex: 1 }}
                          />
                          <Button
                            size="xs"
                            color="indigo"
                            variant="filled"
                            loading={savingKeys[c.id]}
                            onClick={() => handleSaveGeminiKey(c.id)}
                          >
                            {t('save_btn', 'Zapisz')}
                          </Button>
                        </Group>
                        {c.geminiModel && (
                          <Text size="xs" c="dimmed" mt={2}>
                            Model: {c.geminiModel}
                          </Text>
                        )}
                      </Table.Td>

                      {/* Actions */}
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Group gap={4} justify="flex-end" wrap="nowrap">
                          <Tooltip label={t('tokens_action_send_email', 'Wyślij token e-mailem do klienta')}>
                            <ActionIcon color="blue" variant="light" size="sm" onClick={() => handleSendEmail(c.id, c.clientEmail)}>
                              <IconMail size={16} />
                            </ActionIcon>
                          </Tooltip>

                          <Tooltip label={t('tokens_action_test', 'Przetestuj zapytanie proxy dla tego tokenu')}>
                            <ActionIcon color="indigo" variant="light" size="sm" onClick={() => {
                              setTestToken(c.clientToken);
                              setTestModalOpen(true);
                            }}>
                              <IconRobot size={16} />
                            </ActionIcon>
                          </Tooltip>

                          <Tooltip label={t('delete_btn', 'Usuń')}>
                            <ActionIcon color="red" variant="subtle" size="sm" onClick={() => handleDeleteClient(c.id)}>
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Card>
      </Stack>

      {/* MODAL 1: Buy Tokens / Generate Checkout URL */}
      <Modal
        opened={buyModalOpen}
        onClose={() => setBuyModalOpen(false)}
        title={<Title order={4} c="indigo">{t('tokens_buy_modal_title', 'Zakup Pakietu Tokenów (100 PLN + VAT)')}</Title>}
        size="md"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t('tokens_buy_modal_desc', 'Wprowadź adres e-mail klienta, aby wygenerować link płatności Stripe. Domyślny pakiet to 100 PLN netto + 23% VAT (123,00 PLN brutto). Po opłaceniu token jest generowany i wysyłany na podany e-mail.')}
          </Text>

          <TextInput
            label={t('tokens_modal_email_label', 'Adres E-mail Klienta')}
            placeholder="klient@firma.pl"
            value={buyEmail}
            onChange={(e) => setBuyEmail(e.target.value)}
            required
          />

          <Button
            color="indigo"
            size="md"
            fullWidth
            loading={buyLoading}
            onClick={handleGenerateBuyCheckout}
            leftSection={<IconShoppingCart size={18} />}
          >
            {t('tokens_generate_checkout_btn', 'Generuj Link do Płatności (Stripe)')}
          </Button>

          {generatedBuyUrl && (
            <Paper withBorder p="md" radius="md" bg="blue.0">
              <Text size="xs" fw={700} c="indigo.9" mb={4}>
                {t('tokens_checkout_ready_title', 'Link do płatności został wygenerowany:')}
              </Text>
              <TextInput
                size="xs"
                readOnly
                value={generatedBuyUrl}
                rightSection={
                  <CopyButton value={generatedBuyUrl} timeout={2000}>
                    {({ copied, copy }) => (
                      <ActionIcon color={copied ? 'teal' : 'indigo'} variant="subtle" onClick={copy}>
                        {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                      </ActionIcon>
                    )}
                  </CopyButton>
                }
              />
              <Group mt="sm" justify="flex-end">
                <Button
                  component="a"
                  href={generatedBuyUrl}
                  target="_blank"
                  size="xs"
                  color="indigo"
                  rightSection={<IconExternalLink size={14} />}
                >
                  {t('tokens_open_checkout_btn', 'Przejdź do Płatności')}
                </Button>
              </Group>
            </Paper>
          )}
        </Stack>
      </Modal>

      {/* MODAL 2: Add Client Manually */}
      <Modal
        opened={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        title={<Title order={4} c="indigo">{t('tokens_add_modal_title', 'Dodaj Nowego Klienta Tokenów')}</Title>}
        size="md"
      >
        <form onSubmit={handleCreateClient}>
          <Stack gap="md">
            <TextInput
              label={t('tokens_modal_email_label', 'Adres E-mail')}
              placeholder="klient@firma.pl"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              required
            />

            <NumberInput
              label={t('tokens_modal_amount_label', 'Początkowy Stan Tokenów')}
              value={newTokens}
              onChange={(val) => setNewTokens(val || 100000)}
              min={1000}
              step={10000}
            />

            <TextInput
              label={t('tokens_modal_gemini_key_label', 'Klucz Gemini API (opcjonalny)')}
              placeholder="AIzaSy..."
              value={newGeminiKey}
              onChange={(e) => setNewGeminiKey(e.target.value)}
            />

            <TextInput
              label="Model Gemini"
              placeholder="gemini-2.5-flash"
              value={newModel}
              onChange={(e) => setNewModel(e.target.value)}
            />

            <Button type="submit" color="indigo" fullWidth mt="sm">
              {t('tokens_add_confirm_btn', 'Zapisz i Utwórz Token')}
            </Button>
          </Stack>
        </form>
      </Modal>

      {/* MODAL 3: Test Gemini Proxy */}
      <Modal
        opened={testModalOpen}
        onClose={() => setTestModalOpen(false)}
        title={<Title order={4} c="indigo">{t('tokens_test_modal_title', 'Testowanie Proxy Gemini przez Portal')}</Title>}
        size="lg"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t('tokens_test_modal_desc', 'Sprawdź działanie zapytania proxy. Żądanie zostanie przesłane przez portal AILinkB, który zweryfikuje token i przekaże prompt do Gemini AI.')}
          </Text>

          <TextInput
            label={t('tokens_test_token_label', 'Token Klienta')}
            placeholder="TK-..."
            value={testToken}
            onChange={(e) => setTestToken(e.target.value)}
            required
          />

          <TextInput
            label={t('tokens_test_prompt_label', 'Prompt / Treść Zadania')}
            placeholder="Wpisz treść pytania do AI..."
            value={testPrompt}
            onChange={(e) => setTestPrompt(e.target.value)}
            required
          />

          <Button
            color="indigo"
            loading={testLoading}
            onClick={handleRunProxyTest}
            leftSection={<IconSend size={16} />}
          >
            {t('tokens_test_send_btn', 'Wyślij Zadanie do Gemini przez Portal')}
          </Button>

          {testResponse && (
            <Paper withBorder p="md" radius="md" bg="#f8fafc">
              <Text size="xs" fw={700} c="dimmed" mb={4}>
                {t('tokens_test_response_label', 'Odpowiedź Gemini:')}
              </Text>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {testResponse}
              </Text>
            </Paper>
          )}
        </Stack>
      </Modal>
    </Container>
  );
}
